<template>
  <div
    ref="elementRef"
    v-style:bg="backgroundColor"
    :class="{ 'py-6': backgroundColor, 'overflow-x-clip': isEdgeToEdge }"
  >
    <div :class="insetClasses">
      <div v-style:m="containerMargin">
        <div
          v-if="titleBlockVisible"
          :class="[
            mergedConfig.elevatedControls ? 'flex items-end between' : 'f-col md:flex-row md:items-end',
            titleSpacing,
            isCenterAligned
              && [
                'items-center text-center relative',
                { 'min-h-8 px-20': elevatedCarouselActive },
              ],
            { '<md:px-4': isStackedLayout },
            titlePadding,
          ]"
        >
          <vf-carousel-controls
            v-if="elevatedCarouselActive && isSplitPagination"
            :carousel-ref="carousel"
            :class="{ 'absolute left-0 bottom-0': isCenterAligned }"
            hide-right
            :variant="backgroundType"
          />
          <div :class="[titleGapY, { 'mx-a': isCenterAligned }]">
            <component
              :is="titleTag"
              v-if="title"
              v-style:c="titleColor"
              :class="titleStyleClasses"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </component>
            <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
              <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
              <span v-else>{{ subtitle }}</span>
            </p>
            <cms-shared-button
              v-if="textLink && mergedConfig.elevatedControls"
              v-bind="{ ...textLink, ...getTextLinkStyles(content) }"
            />
          </div>
          <vf-carousel-controls
            v-if="elevatedCarouselActive"
            :carousel-ref="carousel"
            :class="{ 'absolute right-0 bottom-0': isCenterAligned }"
            :hide-left="isSplitPagination"
            :variant="backgroundType"
          />
          <cms-shared-button
            v-if="textLink && !mergedConfig.elevatedControls"
            v-bind="{ ...textLink, ...getTextLinkStyles(content) }"
          />
        </div>
        <vf-carousel-elevate
          ref="carousel"
          v-bind="carouselSettings"
          :as-list
          :bleed="isEdgeToEdge"
          :class="[collectionAlignment, paddingClasses]"
          :class-container
          :class-controls
          :class-controls-inner
          :style="content.type === 'VfCanvasCTAButtonCollection' ? { 'padding-block': '1px' } : {}"
          :style-container="{
            '--p': mobileSlidePadding,
            '--p-md': 0,
            ...(sm?.slidePadding ? { '--m': `${spacing[sm.slidePadding]}rem` } : {}),
            ...(md?.slidePadding ? { '--m-md': `${spacing[md.slidePadding]}rem` } : {}),
            ...(lg?.slidePadding ? { '--m-lg': `${spacing[lg.slidePadding]}rem` } : {}),
          }"
        >
          <template #default="{ paused, activeItem }">
            <component
              :is="asList ? 'li' : 'div'"
              v-for="(item, index) in items"
              :key="item.id"
              v-style="{
                'max-w': slideMaxWidth,
                'p': slidePadding,
                'w': getAdjustedSlideWidth(index),
                'display': getVisibilityStyles(item.hideOnBreakpoint),
                ...getPercentHeightCssVars(getEffectivePercentHeight(item), percentHeightHeaderOffsetPx),
              }"
              :class="[
                hasHiddenItems ? {
                  'visible-sm': !item.hideOnBreakpoint.sm,
                  'visible-md': !item.hideOnBreakpoint.md,
                  'visible-lg': !item.hideOnBreakpoint.lg,
                } : {},
                getItemSlidePercentHeightClasses(item),
              ]"
              :data-interval="
                item.media?.[$viewport.breakpoint]?.duration
                  ? item.media?.[$viewport.breakpoint]?.duration * 1000
                  : undefined
              "
              :data-test-id="`${item.type}-${item.id}`"
              :data-tracking-id="item.delocalizedTracking"
            >
              <component
                :is="`lazy-cms-${resolveContentComponent(item, mappings)?.componentType}`"
                v-style="{
                  ...brandStyles[
                    isFullHeight && !getEffectivePercentHeight(item) ? 'containerFullHeight' : 'container'
                  ]?.(headerHeight, promoBarHeight),
                  h: !usesPercentHeight(getEffectivePercentHeight(item))
                    && (isFullScreen && { sm: '50vh', md: '100vh' }),
                }"
                class="h-full"
                :class="[itemClass, getItemPercentHeightClasses(item)]"
                :content="getItemContent(item)"
                :controls="!carouselSettings.showProgress"
                :first-element
                :full-height="isFullScreen || cardsAlignment === 'equal'"
                :index
                :is-active-slide="activeItem === index"
                is-part-of-collection
                :natural-height="cardsAlignment === 'natural'"
                :parent-heading-level="title && +titleTag.charAt(1)"
                :paused
                v-on="getItemListeners(item)"
              />
            </component>
          </template>
          <template
            v-if="carouselSettings.showPagination && items.length"
            #pagination="{ activeItem, total, scrollTo }"
          >
            <template v-if="mergedConfig.elevatedControls">
              <vf-carousel-pagination-dots
                v-if="!titleBlockVisible && carouselSettings.active && carousel?.canScroll"
                :active="activeItem"
                class="justify-center pt-2"
                :total
                :variant="backgroundType"
                @update:active="scrollTo($event)"
              />
            </template>
            <vf-carousel-pagination-dots
              v-else
              :active="activeItem"
              class="justify-center pt-6 "
              show-all-dots
              :total
              :variant="backgroundType"
              @update:active="scrollTo($event)"
            />
          </template>
          <template
            v-if="mergedConfig.elevatedControls && !titleBlockVisible && carouselSettings.active"
            #progressbar="{ currentSlide, progress, scrollTo, slides, togglePlay }"
          >
            <vf-carousel-pagination-dots
              :active="currentSlide.index"
              autoplay
              class="absolute bottom-0 z-4 mb-2 w-full justify-center"
              :progress
              :total="slides"
              @update:active="scrollTo($event)"
              @update:paused="togglePlay"
            />
          </template>
        </vf-carousel-elevate>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { CollectionContent } from '#types/components/cms/collection'
import type { CarouselElevateInstance } from '#types/components/vf/carousel-elevate'
import type { ComponentPercentHeight } from '#types/content'
import { ParentPromotionTrackingKey } from '#content/contexts/promotionTracking'
import mappings from '#content/mappings'

const { content, edgeToEdge, firstElement, pageContext } = defineProps<{
  content: CollectionContent<any>
  firstElement?: boolean
  asList?: boolean
  pageContext?: boolean
  /**
   * Aligns first slide and snap points to the left content inset and lets slides bleed to the viewport edge.
   * Enable-only override; brands can turn it on via the collection config `edgeToEdge` key
   */
  edgeToEdge?: boolean
}>()

const cms = useAppConfig().components.cms
const collectionConfig = cms.collection
const insetClasses = getContentInset(content, cms.section)
const mergedConfig = mergeDeep(
  collectionConfig,
  collectionConfig?.[content.type],
  collectionConfig?.[content.type]?.[content.variant],
)

// Outside a page context the section strips componentSidePaddings/componentMaxWidth,
// so there is no viewport-centered container for the bleed geometry to align to
const isEdgeToEdge = computed(() => pageContext && (edgeToEdge || !!mergedConfig.edgeToEdge))
const {
  brandStyles,
  classControls: defaultClassControls,
  classControlsInner,
  showArrowsOnHoverOnly,
  showArrowsOnHoverThreshold,
  sizeControls,
  spacing,
  itemClass,
  itemsProp,
  titleSpacing,
  titleGapY
} = mergedConfig
const { breakpoints } = useAppConfig().ds
const { promoBarHeight } = useAppConfig().components.vf.header
const header = useHeaderStore()
const { $viewport } = useNuxtApp()
const { width: windowWidth } = useWindowSize()

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)
const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const isFullScreen = computed(() => content.variant === 'twoCardGridFullHeight')

const carousel = useTemplateRef<CarouselElevateInstance>('carousel')

const isButtonCollectionOverflowing = ref(false)

const { lg, md, sm } = content.breakpoints

// Item-level componentPercentHeight takes priority; collection-level acts as a fallback for all items.
const getEffectivePercentHeight = (item: { componentPercentHeight?: ComponentPercentHeight }) =>
  item?.componentPercentHeight ?? content.componentPercentHeight

const usesPercentHeight = (componentPercentHeight?: ComponentPercentHeight) =>
  hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
  || hasPercentHeightAtBreakpoint(componentPercentHeight?.large)

const getItemSlidePercentHeightClasses = (item: { componentPercentHeight?: ComponentPercentHeight }) => {
  const cph = getEffectivePercentHeight(item)
  const smMd = hasPercentHeightAtBreakpoint(cph?.small)
  const lg = hasPercentHeightAtBreakpoint(cph?.large)
  return {
    'min-h-0': smMd || lg,
    '<md:o:h-$h <md:overflow-hidden': smMd,
    'o:lg:h-$h-lg lg:overflow-hidden': lg,
    'o:h-$h': smMd && lg,
    'o:<lg:h-$h': smMd && !lg,
  }
}

// ===================================
// Temporary Solution for stacked collection
// start
// ===================================
const isStackedLayout = content?.name!.indexOf('[stacked]') >= 0

if (isStackedLayout) {
  // eslint-disable-next-line vue/no-mutating-props
  content.componentSidePaddings = { sm: true, lg: true }
  if (lg) {
    lg.type = 'grid'
    lg.slideWidth = '100%'
  }

  if (md) {
    md.type = 'grid'
    md.slideWidth = '100%'
  }
}
// ===================================
// Temporary Solution for stacked collection
// end
// ===================================

const {
  alignment,
  backgroundColor,
  backgroundType,
  componentSidePaddings,
  equalTargetSize,
  isFullHeight,
  name,
  promotionTracking,
  subtitle,
  subtitleColor,
  subtitleHtml,
  subtitleStyle,
  textLink,
  title,
  titleAlignment,
  titleColor,
  titleHeadingTag,
  titleHtml,
  titlePosition,
  titleStyle,
  variant,
  firstCardSize,
  cardsAlignment,
  paginationPosition
} = content

const titleBlockVisible = title || subtitle || textLink
const isCenterAligned = [alignment, titleAlignment, titlePosition].includes('center')
const isSplitPagination = paginationPosition === 'split'

const getItemPercentHeightClasses = (item: { componentPercentHeight?: ComponentPercentHeight }) => {
  const cph = getEffectivePercentHeight(item)
  return {
    'children:h-inherit': isFullHeight && !cph,
    'md:children:h-inherit': !isFullHeight && hasPercentHeightAtBreakpoint(cph?.small),
    'lg:children:h-inherit': !isFullHeight && hasPercentHeightAtBreakpoint(cph?.large),
  }
}

const getItemContent = (item) => {
  const { onItemClick: _, ...rest } = item
  return {
    ...rest,
    componentSidePaddings,
    isFullHeight,
    componentPercentHeight: getEffectivePercentHeight(item),
  }
}

const getItemListeners = (item: any) =>
  typeof item.onItemClick === 'function' ? { click: () => item.onItemClick(item.target) } : {}

const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

provide(ParentPromotionTrackingKey, {
  parentPromotionName: promotionTracking?.promotionName,
  parentHandlePromotionClick: handlePromotionClick
})

const items = computed(() => (itemsProp ? content[itemsProp] : content.items) || [])
const hasHiddenItems = items.value.some((item) => Object.values(item?.hideOnBreakpoint || {}).some(Boolean))

const collectionAlignment = computed(() => {
  if (alignment === 'center') {
    return {
      'lg:f-col lg:items-center': lg?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '~md:f-col ~md:items-center': md?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '<md:f-col <md:items-center': sm?.type === 'carousel' && !isButtonCollectionOverflowing.value
    }
  }
  else if (alignment === 'right') {
    return {
      'lg:f-col lg:items-end': lg?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '~md:f-col ~md:items-end': md?.type === 'carousel' && !isButtonCollectionOverflowing.value,
      '<md:f-col <md:items-end': sm?.type === 'carousel' && !isButtonCollectionOverflowing.value
    }
  }
})

const getItemWidth = (w?: string) => {
  if (w === 'auto') return `${100 / items.value.length}%`
  return w || 'auto'
}

const getItemMaxWidth = (maxW?: string) => maxW || 'none'

const slideWidth: Record<keyof Responsive, string> = Object.keys(breakpoints).reduce((acc, cur) => {
  const width = getItemWidth(
    getValueForBreakpoint(cur as keyof Responsive, equalTargetSize)
    && content.type === 'VfCanvasCTAButtonCollection'
    && content.breakpoints[cur].type !== 'grid'
      ? `calc(12.5rem + ${2 * spacing[content.breakpoints[cur].slidePadding] || 0}rem)`
      : content.breakpoints[cur].slideWidth
  )

  return { ...acc, [cur]: width }
}, {} as Record<keyof Responsive, string>)

const getAdjustedSlideWidth = (index: number) => {
  const mapping = {
    threeCardGrid: !index && { sm: slideWidth.sm, md: '50%' },
    twoCardGrid: firstCardSize
      && { sm: slideWidth.sm, md: index % 2 ? `calc(100% - ${firstCardSize})` : firstCardSize }
  }

  return mapping[variant] || slideWidth
}

const cardAlignmentClass = {
  top: 'items-start',
  middle: 'items-center',
  bottom: 'items-end',
  natural: 'items-start',
  full: 'items-stretch'
}

const slideMaxWidth: Record<keyof Responsive, string> = {
  sm: getItemMaxWidth(sm?.slideMaxWidth),
  md: getItemMaxWidth(md?.slideMaxWidth),
  lg: getItemMaxWidth(lg?.slideMaxWidth)
}

// Mobile carousel centring
const mobileSlideWidth = Number.parseInt(slideWidth.sm)
const mobileSlidePadding = Number.isNaN(mobileSlideWidth) ? 0 : `0 0 0 ${(100 - mobileSlideWidth) / 2}%`

const containerMargin: Record<keyof Responsive, any> = {
  sm: sm?.margin && `0 ${sm.margin}`,
  md: md?.margin && `0 ${md.margin}`,
  lg: lg?.margin && `0 ${lg.margin}`
}

// Create padding for each slide
const slidePadding = Object.keys(breakpoints)
  .reduce((acc, bp) => ({
    ...acc,
    ...(content.breakpoints[bp]?.slidePadding && {
      [bp]: `${spacing[content.breakpoints[bp].slidePadding] || 0}rem`
    })
  }), {})

const classControls = ((): string[] => {
  const hiddenClass = {
    sm: '<md:hidden',
    md: '~md:hidden',
    lg: 'lg:hidden'
  }

  return Object.keys(hiddenClass).reduce((acc, bp) => {
    if (content.breakpoints[bp]?.type === 'grid') return [...acc, hiddenClass[bp]]
    // show arrows by default if showArrows is not set
    return [...acc, ...(content.breakpoints[bp]?.showArrows ?? true) ? [] : [hiddenClass[bp]]]
  }, [defaultClassControls])
})()

const isItemTextLink = (bp: keyof Responsive, index: number) => getValueForBreakpoint(bp, items.value.at(index)?.target?.style) === 'text-link'

const paddingClasses = content.type === 'VfCanvasCTAButtonCollection'
  && {
    '<md:pl-4': !componentSidePaddings?.sm && isItemTextLink('sm', 0),
    '~md:pl-4': !componentSidePaddings?.sm && isItemTextLink('md', 0),
    'lg:pl-4': !componentSidePaddings?.lg && isItemTextLink('lg', 0),
    '<md:pr-4': !componentSidePaddings?.sm && isItemTextLink('sm', -1),
    '~md:pr-4': !componentSidePaddings?.sm && isItemTextLink('md', -1),
    'lg:pr-4': !componentSidePaddings?.lg && isItemTextLink('lg', -1)
  }

/**
 * Get style classes for carousel containers
 */
const classContainer = [
  // Mobile slides centring
  'scroll-pa-$p',
  'md:scroll-pa-$p-md',
  // Create a negative margin for the container to compensate slides paddings
  sm?.slidePadding ? '<md:-m-$m' : '',
  md?.slidePadding ? '~md:-m-$m-md' : '',
  lg?.slidePadding ? 'lg:-m-$m-lg' : '',
  // Carousel vs Grid Layout
  {
    '<md:wrap': sm?.type === 'grid',
    '~md:wrap': md?.type === 'grid',
    'lg:wrap': lg?.type === 'grid'
  },
  {
    'items-center': content.type === 'VfCanvasCTAButtonCollection',
    'gap-y-4 md:gap-y-6': content.type === 'VfCanvasCategoryCardCollection' && content.variant.includes('elevated-grid'),
    'justify-center': content.type === 'VfCanvasCategoryCardCollection' && content.variant.includes('elevated-grid') && items.value.length <= 2,
    'between': content.type === 'VfCanvasLinkedCardCollection' && content.variant === 'threeCardGrid_StackedOnSmall'
  },
  cardsAlignment && cardAlignmentClass[cardsAlignment]
]

const carouselSettings = computed(() => {
  const {
    autoplay,
    autorotateTime = 0,
    loop,
    showArrows = mergedConfig.showArrows,
    showIndicators = mergedConfig.showIndicators,
    showProgress,
    type
  } = content.breakpoints[$viewport.breakpoint] || {}
  const slides = hasHiddenItems ? `.visible-${$viewport.breakpoint}` : null

  return {
    active: type === 'carousel',
    ...(isEdgeToEdge.value && { align: 'start' as const }),
    autoplay,
    slides,
    dragFree: content.type === 'VfCanvasCTAButtonCollection',
    highlightCenter: content.variant === 'highlightCenter',
    interval: autorotateTime * 1000,
    // Loop recycling teleports slides at the embla viewport boundary, which is visible in the unclipped bleed area
    loop: !isEdgeToEdge.value && loop,
    showArrows,
    showArrowsOnHoverOnly,
    showArrowsOnHoverThreshold,
    showPagination: showIndicators,
    showProgress,
    sizeControls,
  }
})
const elevatedCarouselActive = computed(() => mergedConfig.elevatedControls && carouselSettings.value.active)

const titleTag = computed(() => titleHeadingTag || (firstElement ? 'h1' : 'h2'))

const titlePadding = (content.type === 'VfCanvasCTAButtonCollection' && variant === 'flex') || !pageContext ? '' : getInvertedPaddings(content, cms.section)

const titleStyleClasses = [titleStyle, content.type === 'VfCanvasCTAButtonCollection' && variant === 'flex' && 'fw-bold'].filter(Boolean)

watchEffect(() => {
  if (carousel.value && content.type === 'VfCanvasCTAButtonCollection')
    isButtonCollectionOverflowing.value = carousel.value.carousel.containerWidth > windowWidth.value
})

// Re-initialize carousel when breakpoint changes if there are hidden items
// This ensures the correct size of the slides is calculated when loop is enabled
if (hasHiddenItems) {
  watch(() => $viewport.breakpoint, () => {
    if (carousel.value) {
      carousel.value.emblaApi.value?.reInit({
        ...carouselSettings.value,
        slides: `.visible-${$viewport.breakpoint}`
      })
    }
  })
}
</script>
